import React from 'react';

import {
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiMaximize2,
  FiX,
} from 'react-icons/fi';

import { DocumentationHeading } from 'api/documentation';

interface Props {
  title: string;
  /** The page's top-level sections: the steps of a guide. */
  steps: DocumentationHeading[];
  currentStep: number;
  onStep: (id: string) => void;
  onExpand: () => void;
  onClose: () => void;
}

/**
 * Page title, a step picker with previous/next, and expand/close. The picker
 * stands in for the page's nav and outline, which don't fit in the panel.
 */
export const DocsPanelHeader = ({
  title,
  steps,
  currentStep,
  onStep,
  onExpand,
  onClose,
}: Props) => {
  const step = steps[currentStep];
  return (
    <Box
      px={4}
      pt={3}
      pb={2}
      flexShrink={0}
      borderBottom="1px solid"
      borderColor="gray.200"
    >
      <Flex align="center" gap={1}>
        <Text flex={1} minW={0} fontSize="15px" fontWeight={600} noOfLines={1}>
          {title}
        </Text>
        <Tooltip label="Open the full page" placement="bottom">
          <IconButton
            aria-label="Open the full page"
            icon={<Icon as={FiMaximize2} />}
            size="sm"
            variant="ghost"
            onClick={onExpand}
          />
        </Tooltip>
        <Tooltip label="Close the guide" placement="bottom">
          <IconButton
            aria-label="Close the guide"
            flexShrink={0}
            icon={<Icon as={FiX} />}
            size="sm"
            variant="ghost"
            onClick={onClose}
          />
        </Tooltip>
      </Flex>
      {steps.length > 1 && (
        <Flex align="center" gap={1} mt={1.5}>
          <IconButton
            aria-label="Previous step"
            icon={<Icon as={FiChevronLeft} />}
            size="xs"
            variant="ghost"
            isDisabled={currentStep <= 0}
            onClick={() => onStep(steps[currentStep - 1].id)}
          />
          <Menu placement="bottom-start" isLazy>
            <MenuButton
              as={Button}
              flex={1}
              minW={0}
              size="xs"
              variant="ghost"
              fontWeight={400}
              textAlign="left"
              rightIcon={<Icon as={FiChevronDown} />}
            >
              <Text noOfLines={1} fontSize="13px" color="primary">
                {currentStep + 1}/{steps.length} · {step?.text}
              </Text>
            </MenuButton>
            <MenuList maxH="60vh" overflowY="auto" zIndex={5001}>
              {steps.map((item, index) => (
                <MenuItem
                  key={item.id}
                  fontSize="14px"
                  color={index === currentStep ? 'primary' : undefined}
                  onClick={() => onStep(item.id)}
                >
                  {index + 1}. {item.text}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
          <IconButton
            aria-label="Next step"
            icon={<Icon as={FiChevronRight} />}
            size="xs"
            variant="ghost"
            isDisabled={currentStep >= steps.length - 1}
            onClick={() => onStep(steps[currentStep + 1].id)}
          />
        </Flex>
      )}
    </Box>
  );
};
